"use client";

import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { SignupForm } from "@/components/signup-form";
import { X, Loader2 } from "lucide-react";
import {
  LibraryBig as LibraryIcon,
  LayoutGrid,
  Sparkles,
  Activity,
  Bell,
  TrendingUp,
  Play,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { LibraryCard } from "@/components/library/LibraryCard";
import {
  LIBRARY_ITEMS,
  TABS,
  SECTION_TITLE,
  type LibraryItem,
  type LibraryKind,
} from "@/lib/mock/library";
import { apiClient } from "@/lib/api-client";
import { skillToLibraryItem } from "@/lib/skills-map";
import { signalToLibraryItem } from "@/lib/signals-map";
import { featuredAlertToLibraryItem } from "@/lib/automations-map";
import { caseStudyToLibraryItem } from "@/lib/blogs-map";
import { LIBRARY_VIDEOS } from "@/lib/videos-catalog";
import { LibraryVideoModal } from "@/components/library/LibraryVideoModal";
import { CASE_STUDIES } from "@/utils/caseStudiesCatalog";
import { parseLibraryTab, type LibraryTab } from "@/lib/library-routes";

const SECTION_BLURB: Record<LibraryKind, string> = {
  skill: "Saved AI research workflows you can run in one click.",
  signal: "Live, track-recorded trading signals from the Xynth engine.",
  automation: "Scheduled AI market scans delivered straight to your inbox.",
  idea: "Real traders, real results, real Xynth workflows.",
  video: "Short walkthroughs of everything Xynth can do.",
};

const TAB_ICON: Record<LibraryTab, typeof LayoutGrid> = {
  all: LayoutGrid,
  alerts: Bell,
  skill: Sparkles,
  signal: Activity,
  automation: Bell,
  idea: TrendingUp,
  video: Play,
};

export default function LibraryClient() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const tab = parseLibraryTab(searchParams?.get("tab") ?? null);

  const setTab = useCallback(
    (next: LibraryTab) => {
      const params = new URLSearchParams(searchParams?.toString() ?? "");
      if (next === "all") {
        params.delete("tab");
      } else {
        params.set("tab", next);
      }
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [router, pathname, searchParams],
  );
  const [realSkills, setRealSkills] = useState<LibraryItem[] | null>(null);
  const [realSignals, setRealSignals] = useState<LibraryItem[] | null>(null);
  const [realAutomations, setRealAutomations] = useState<LibraryItem[] | null>(
    null,
  );
  const [activeVideo, setActiveVideo] = useState<LibraryItem | null>(null);
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Blogs come from the local case-studies catalog (published only).
  const blogs = useMemo<LibraryItem[]>(
    () =>
      CASE_STUDIES.filter((s) => s.isPublished).map(caseStudyToLibraryItem),
    [],
  );

  useEffect(() => {
    apiClient
      .listPublicSkills({ limit: 50 })
      .then((res) => {
        const mapped = (res.skills || []).map(skillToLibraryItem);
        if (mapped.length) setRealSkills(mapped);
      })
      .catch(() => {
        // keep mock skills as fallback so the page never renders empty
      });
    apiClient
      .listPublicSignals({ limit: 50 })
      .then((res) => {
        const mapped = (res.signals || []).map(signalToLibraryItem);
        if (mapped.length) setRealSignals(mapped);
      })
      .catch(() => {
        // keep mock signals as fallback so the page never renders empty
      });
    apiClient
      .listFeaturedAlerts({ limit: 50 })
      .then((res) => {
        const mapped = (res.alerts || []).map(featuredAlertToLibraryItem);
        if (mapped.length) setRealAutomations(mapped);
      })
      .catch(() => {
        // keep mock automations as fallback so the page never renders empty
      });
  }, []);

  // Merge real skills + signals + automations + blogs (from the backend /
  // catalog) with the mock cards for the content types we haven't wired yet.
  const allItems = useMemo<LibraryItem[]>(() => {
    const skills = realSkills ?? LIBRARY_ITEMS.filter((i) => i.kind === "skill");
    const signals =
      realSignals ?? LIBRARY_ITEMS.filter((i) => i.kind === "signal");
    const automations =
      realAutomations ?? LIBRARY_ITEMS.filter((i) => i.kind === "automation");
    const ideas = blogs.length
      ? blogs
      : LIBRARY_ITEMS.filter((i) => i.kind === "idea");
    return [...skills, ...signals, ...automations, ...ideas, ...LIBRARY_VIDEOS];
  }, [realSkills, realSignals, realAutomations, blogs]);

  const sections = useMemo<LibraryKind[]>(
    () => ["skill", "signal", "automation", "idea", "video"],
    [],
  );

  const handleSelect = (item: LibraryItem) => {
    // Guests can browse the grid, but opening anything requires sign-in.
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (item.kind === "skill") {
      router.push(`/skills/${item.id}`);
    } else if (item.kind === "signal" || item.kind === "automation") {
      router.push(`/alerts/${item.id}`);
    } else if (item.kind === "idea") {
      router.push(`/blog/${item.id}`);
    } else if (item.kind === "video" && item.youtubeUrl) {
      setActiveVideo(item);
    }
  };

  return (
    <>
    <div className="w-full max-w-[1180px] mx-auto px-6 sm:px-14 pt-6 pb-28">
      {/* Hero */}
      <div className="mb-6 flex items-start justify-between gap-6">
        <div>
          <div className="mb-3 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-400">
              The Library
            </span>
          </div>
          <h1 className="text-3xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl">
            Everything Xynth can do, in one place.
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
            Skills, signals, automations and stories. Run deep research, track
            live trades, and put the whole engine to work from a single page.
          </p>
        </div>
        <LibraryIcon
          className="hidden h-32 w-32 shrink-0 self-start text-white/90 sm:block lg:h-40 lg:w-40"
          strokeWidth={1.25}
        />
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-20 -mx-6 mb-6 border-b border-white/[0.07] bg-[#09090b]/85 px-6 py-2 backdrop-blur-xl sm:-mx-14 sm:px-14">
        <div className="flex flex-wrap items-center gap-1.5">
          {TABS.map((t) => {
            const Icon = TAB_ICON[t.value];
            return (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                  tab === t.value
                    ? "bg-white text-[#09090b]"
                    : "text-white/90 hover:bg-white/[0.06] hover:text-white",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {tab === "all" || tab === "alerts" ? (
        <div className="space-y-12">
          {(tab === "alerts"
            ? (["signal", "automation"] as LibraryKind[])
            : sections
          ).map((kind) => {
            const items = allItems.filter((i) => i.kind === kind);
            return (
              <section key={kind}>
                <div className="mb-5">
                  <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    {SECTION_TITLE[kind]}
                  </h2>
                  <p className="mt-1.5 text-base text-white/80">
                    {SECTION_BLURB[kind]}
                  </p>
                </div>
                <Grid>
                  {items.map((item) => (
                    <LibraryCard
                      key={item.id}
                      item={item}
                      onSelect={() => handleSelect(item)}
                    />
                  ))}
                </Grid>
              </section>
            );
          })}
        </div>
      ) : (
        <div>
          <div className="mb-5">
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              {SECTION_TITLE[tab as LibraryKind]}
            </h2>
            <p className="mt-1.5 text-base text-white/80">
              {SECTION_BLURB[tab as LibraryKind]}
            </p>
          </div>
          <Grid>
            {allItems
              .filter((i) => i.kind === tab)
              .map((item) => (
                <LibraryCard
                  key={item.id}
                  item={item}
                  onSelect={() => handleSelect(item)}
                />
              ))}
          </Grid>
        </div>
      )}
    </div>
    <LibraryVideoModal item={activeVideo} onClose={() => setActiveVideo(null)} />
    {showAuthModal && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-6 backdrop-blur-sm"
        onClick={() => setShowAuthModal(false)}
      >
        <div
          className="relative w-full max-w-sm rounded-lg border border-border bg-background p-6 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={() => setShowAuthModal(false)}
            className="absolute right-3 top-3 rounded-md p-1 text-white/50 transition-colors hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
          <Suspense
            fallback={
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
            }
          >
            <SignupForm />
          </Suspense>
        </div>
      </div>
    )}
    </>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: 0.04 } },
      }}
      className="grid grid-cols-2 gap-4 lg:grid-cols-4"
    >
      {Array.isArray(children)
        ? children.map((child, i) => (
            <motion.div
              key={i}
              variants={{
                hidden: { opacity: 0, y: 12 },
                show: { opacity: 1, y: 0 },
              }}
              className="h-full"
            >
              {child}
            </motion.div>
          ))
        : children}
    </motion.div>
  );
}
